import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Tag,
  Percent,
  DollarSign,
  Gift,
  Calendar,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Package,
  Copy,
  Share2,
  Edit,
  Trash2,
  Crown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DiscountCode } from '../../services/discountService';
import { formatCurrency, formatDate, formatPercentage } from '../../lib/utils';
import { toast } from 'sonner';

interface ViewDiscountCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  discount: DiscountCode | null;
  onEdit?: (discount: DiscountCode) => void;
  onShare?: (discount: DiscountCode) => void;
  onDelete?: (discount: DiscountCode) => void;
}

export function ViewDiscountCodeModal({ 
  isOpen, 
  onClose, 
  discount,
  onEdit,
  onShare,
  onDelete
}: ViewDiscountCodeModalProps) {
  if (!discount) return null;

  const handleClose = () => {
    onClose();
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(discount.code);
      toast.success('Discount code copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy discount code');
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          color: 'bg-green-100 text-green-800',
          label: 'Active',
          icon: CheckCircle
        };
      case 'inactive':
        return {
          color: 'bg-gray-100 text-gray-800',
          label: 'Inactive',
          icon: Clock
        };
      case 'expired':
        return {
          color: 'bg-red-100 text-red-800',
          label: 'Expired',
          icon: AlertCircle
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          label: status,
          icon: Clock
        };
    }
  };

  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'percentage':
        return {
          color: 'bg-blue-100 text-blue-800',
          label: 'Percentage',
          icon: Percent
        };
      case 'fixed':
        return {
          color: 'bg-green-100 text-green-800',
          label: 'Fixed Amount',
          icon: DollarSign
        };
      case 'free_shipping':
        return {
          color: 'bg-purple-100 text-purple-800',
          label: 'Free Shipping',
          icon: Gift
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          label: type,
          icon: Tag
        };
    }
  };

  const statusConfig = getStatusConfig(discount.status);
  const typeConfig = getTypeConfig(discount.type);
  const StatusIcon = statusConfig.icon;
  const TypeIcon = typeConfig.icon;
  const usagePercentage = (discount.currentUses / discount.maxUses) * 100;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/80 px-8 py-6">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-20"></div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Tag className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Discount Code Details
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      View and manage discount code information
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClose}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0 rounded-lg"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)] bg-gradient-to-b from-gray-50/50 to-white">
              <div className="space-y-8">
                {/* Code and Status Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-green/5 px-6 py-4 border-b border-jamaica-green/20">
                    <div className="flex items-center gap-2">
                      <Tag className="h-5 w-5 text-jamaica-green" />
                      <h3 className="text-lg font-semibold text-gray-900">Discount Code</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <code className="font-mono font-bold text-2xl text-jamaica-green bg-jamaica-green/10 px-4 py-2 rounded-xl border border-jamaica-green/20">
                            {discount.code}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={handleCopyCode}
                            className="h-10 w-10 p-0 hover:bg-jamaica-green/10 rounded-xl"
                          >
                            <Copy className="w-4 h-4 text-jamaica-green" />
                          </Button>
                        </div>
                        <p className="text-gray-600 text-lg leading-relaxed">{discount.description}</p>
                      </div>
                      <Badge className={`${statusConfig.color} px-3 py-1 text-sm font-medium`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>
                </motion.div>

                {/* Key Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Discount Type & Value */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                          <TypeIcon className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Discount Value</h3>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <Badge className={`${typeConfig.color} px-3 py-1 text-sm font-medium`}>
                        <TypeIcon className="w-3 h-3 mr-1" />
                        {typeConfig.label}
                      </Badge>
                      <div className="text-3xl font-bold text-gray-900">
                        {discount.type === 'percentage' ? (
                          `${discount.value}%`
                        ) : discount.type === 'fixed' ? (
                          formatCurrency(discount.value)
                        ) : (
                          'Free Shipping'
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {/* Usage Statistics */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 px-6 py-4 border-b border-emerald-100">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Usage Statistics</h3>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Used</span>
                        <span className="font-bold text-lg">{discount.currentUses} / {discount.maxUses}</span>
                      </div>
                      <div className="space-y-2">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-emerald-500 to-green-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-500 text-center">
                          {usagePercentage.toFixed(1)}% of limit used
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Rush'd Access */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="bg-white rounded-2xl shadow-sm border border-amber-100 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-200">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-amber-500 rounded-lg flex items-center justify-center">
                          <Crown className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Rush'd Access</h3>
                      </div>
                    </div>
                    <div className="p-6 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Availability</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {discount.membersOnly ? "Rush'd members only" : "All customers"}
                        </p>
                        {discount.membersOnly && (
                          <p className="text-sm text-gray-600 mt-1">
                            Non-members will see a prompt to join JamPack'd Rush'd when applying this code.
                          </p>
                        )}
                      </div>
                      <Badge className={discount.membersOnly ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-gray-100 text-gray-700 border-gray-200"}>
                        {discount.membersOnly ? (
                          <div className="flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            Rush'd Only
                          </div>
                        ) : (
                          "Available to all"
                        )}
                      </Badge>
                    </div>
                  </motion.div>

                  {/* Validity Period */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-purple-100">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Validity Period</h3>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Start Date</span>
                          <span className="font-semibold text-gray-900">{formatDate(discount.startDate)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">End Date</span>
                          <span className="font-semibold text-gray-900">{formatDate(discount.endDate)}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Requirements */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-orange-100">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Requirements</h3>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Min Order</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(discount.minOrderAmount)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Products</span>
                          <span className="font-semibold text-gray-900 capitalize">
                            {discount.applicableProducts.join(', ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Timeline Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-gradient-to-r from-gray-500 to-slate-600 rounded-lg flex items-center justify-center">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">Timeline</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Created</span>
                        <span className="font-semibold text-gray-900">{formatDate(discount.createdAt)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Last Updated</span>
                        <span className="font-semibold text-gray-900">{formatDate(discount.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 px-8 py-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {onShare && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onShare(discount)}
                      className="flex items-center gap-2"
                    >
                      <Share2 className="w-4 h-4" />
                      Share Code
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {onEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(discount)}
                      className="flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Code
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(discount)}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Code
                    </Button>
                  )}
                  <Button onClick={handleClose} variant="default">
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 
